import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Simple Theme Toggle Button
 * Switches between light and dark mode
 */
export function ThemeToggle({ variant = "ghost", size = "icon" }) {
  const { theme, toggleTheme } = useTheme();

  return (
    <Button
      variant={variant}
      size={size}
      onClick={toggleTheme}
      className="relative"
      data-testid="theme-toggle"
    >
      <Sun className={`h-5 w-5 transition-all ${theme === 'dark' ? 'rotate-90 scale-0' : 'rotate-0 scale-100'}`} />
      <Moon className={`absolute h-5 w-5 transition-all ${theme === 'dark' ? 'rotate-0 scale-100' : '-rotate-90 scale-0'}`} />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}

/**
 * Theme Toggle with Dropdown Menu
 * Allows selection of light, dark, or system preference
 */
export function ThemeToggleDropdown({ variant = "outline", size = "sm" }) {
  const { theme, setTheme } = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className="gap-2" data-testid="theme-dropdown">
          {theme === 'dark' ? (
            <Moon className="h-4 w-4" />
          ) : (
            <Sun className="h-4 w-4" />
          )}
          <span className="hidden sm:inline capitalize">{theme}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuItem 
          onClick={() => setTheme('light')}
          className={theme === 'light' ? 'bg-slate-100 dark:bg-slate-800' : ''}
        >
          <Sun className="h-4 w-4 mr-2" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTheme('dark')}
          className={theme === 'dark' ? 'bg-slate-100 dark:bg-slate-800' : ''}
        >
          <Moon className="h-4 w-4 mr-2" />
          Dark
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact Theme Toggle for Mobile
 */
export function ThemeToggleCompact() {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative w-14 h-7 rounded-full transition-colors duration-300
        ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}
      `}
      data-testid="theme-toggle-compact"
    >
      {/* Track icons */}
      <Sun className={`absolute left-1.5 top-1.5 h-4 w-4 text-amber-500 transition-opacity ${theme === 'dark' ? 'opacity-30' : 'opacity-100'}`} />
      <Moon className={`absolute right-1.5 top-1.5 h-4 w-4 text-blue-400 transition-opacity ${theme === 'dark' ? 'opacity-100' : 'opacity-30'}`} />
      
      {/* Sliding circle */}
      <span 
        className={`
          absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300
          ${theme === 'dark' ? 'left-7' : 'left-0.5'}
        `}
      />
    </button>
  );
}

export default ThemeToggle;
